"use client";

import { type FC, type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal, type ModalProps } from "../Modal";
import { ProgressBar } from "../ProgressBar";
import { Form } from "../Form";
import { FormRow } from "../FormRow";
import { SubmitButton } from "../Button";
import { TextField } from "../TextField";
import { TextArea } from "../TextArea";
import { InputAddOn } from "../InputAddOn";
import {
  FileUploadField,
  type FileUploadFieldChangeHandler,
} from "../FileUploadField";
import { Flex } from "../Flex";

export type BugReportModalProps = ModalProps & {
  onClose?: () => void;
  onSubmit?: (data: {
    name?: string;
    email?: string;
    message?: string;
    file?: File;
  }) => void;
  loading?: boolean;
  defaultEmail?: string;
  trailing?: ReactNode;
};

/**
 * @todo Move to Vite
 */
export const BugReportModal: FC<BugReportModalProps> = ({
  loading = false,
  defaultEmail = "",
  trailing,
  onSubmit,
  onClose,
  ...props
}) => {
  const { t } = useTranslation();

  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState(defaultEmail);
  const [message, setMessage] = useState("");

  const handleSubmit = () => {
    onSubmit?.({
      name: name || undefined,
      email: email || undefined,
      message: message || undefined,
      file: file || undefined,
    });
  };

  const handleFileChange: FileUploadFieldChangeHandler = (files) => {
    if (files?.[0]) {
      setFile(files[0]);
    }
  };

  return (
    <Modal
      title={t("bugReport.modalTitle")}
      placement="center"
      onClose={onClose}
      closeDisabled={loading}
      closeable
      {...props}
    >
      {loading && <ProgressBar position="absolute" variant="infinite" />}

      <Form disabled={loading} onSubmit={handleSubmit}>
        <FormRow label={t("bugReport.email")}>
          <TextField
            name="email"
            value={email}
            onChange={setEmail}
            leading={<InputAddOn icon="email" variant="leading" />}
            validation={["email"]}
          />
        </FormRow>

        <FormRow label={t("bugReport.file")}>
          <FileUploadField
            onChange={handleFileChange}
            leading={<InputAddOn icon="attach" variant="leading" />}
            accept=".jpg,.jpeg,.png"
          />
        </FormRow>

        <FormRow label={t("bugReport.title")}>
          <TextField value={name} onChange={setName} />
        </FormRow>

        <FormRow label={t("bugReport.description")} variant="vertical">
          <TextArea
            name="message"
            value={message}
            onChange={setMessage}
            autoFocus
            validation={["required"]}
          />
        </FormRow>

        <div />

        <Flex align="center" justify="space-between">
          <SubmitButton variant="secondary" size="s">
            {t("bugReport.submit")}
          </SubmitButton>
          {trailing}
        </Flex>
      </Form>
    </Modal>
  );
};
